import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { BodyText, BodyLarge, Caption, Container, Flex, Spacer } from './ui';
import AppHeader from './Header';
import { FadeInContainer, FadeInUpContainer } from '../styles/animations';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';

interface ContactPageProps {
  onNavigateTo?: (page: string, data?: any) => void;
}

// ─── Animations ────────────────────────────────────────────────────────────
const drawLine = keyframes`
  from { width: 0; }
  to { width: 60px; }
`;

// ─── Page Container ────────────────────────────────────────────────────────
const PageContainer = styled.div`
  background: ${getColor('neutral.white')};
  min-height: 100vh;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  overflow-x: hidden;
`;

// ─── Hero ──────────────────────────────────────────────────────────────────
const HeroSection = styled.section`
  padding: 140px ${getSpacing('lg')} ${getSpacing('2xl')};
  text-align: center;

  @media (max-width: 768px) {
    padding: 120px ${getSpacing('md')} ${getSpacing('xl')};
  }
  @media (max-width: 480px) {
    padding: 100px ${getSpacing('sm')} ${getSpacing('lg')};
  }
`;

const PageTitle = styled.h1`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: clamp(2rem, 5vw, 2.85rem);
  font-weight: 300;
  letter-spacing: -0.02em;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')};
`;

const PageSubtitle = styled.p`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.1rem;
  font-weight: 300;
  font-style: italic;
  color: ${getColor('primary.600')};
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.7;
`;

const GoldLine = styled.div`
  width: 60px;
  height: 2px;
  background: #CAB96E;
  margin: ${getSpacing('lg')} auto 0;
  animation: ${drawLine} 1s ease-out forwards;
`;

// ─── Section Divider (matching HomePage) ───────────────────────────────────
const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${getSpacing('xl')};

  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 120px;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, #CAB96E 50%, transparent 100%);
  }

  & > span {
    color: #CAB96E;
    font-size: 0.5rem;
    margin: 0 ${getSpacing('md')};
    opacity: 0.7;
  }
`;

// ─── Main Content ──────────────────────────────────────────────────────────
const ContactContent = styled.section`
  padding: ${getSpacing('2xl')} 0 ${getSpacing('3xl')};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: ${getSpacing('2xl')};
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('xl')};
  }
`;

// ─── Contact Info Card ─────────────────────────────────────────────────────
const ContactInfoCard = styled.div`
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.100')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  padding: ${getSpacing('2xl')};
  transition: all ${DesignSystem.motion.duration.normal} ease;

  &:hover {
    border-color: #CAB96E;
    box-shadow: ${getShadow('soft')};
  }
`;

const CompanyName = styled.div`
  margin-bottom: ${getSpacing('xl')};
  padding-bottom: ${getSpacing('lg')};
  border-bottom: 2px solid ${getColor('primary.100')};

  h3 {
    color: ${getColor('primary.900')};
    font-size: 1.125rem;
    font-weight: 400;
    letter-spacing: -0.01em;
    margin: 0;
    font-family: ${DesignSystem.typography.fonts.display};
  }

  .brand-accent {
    color: #CAB96E;
  }
`;

const InfoItem = styled.div`
  margin-bottom: ${getSpacing('lg')};

  &:last-child {
    margin-bottom: 0;
  }

  .label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: #CAB96E;
    text-transform: uppercase;
    margin-bottom: ${getSpacing('xs')};
    display: block;
  }

  .value {
    font-size: 1rem;
    color: ${getColor('primary.900')};
    line-height: 1.6;
    font-weight: 400;
  }

  a.value {
    text-decoration: none;
    transition: color ${DesignSystem.motion.duration.fast} ease;
    &:hover { color: #CAB96E; }
  }
`;

// ─── Map Container ─────────────────────────────────────────────────────────
const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  border-radius: ${DesignSystem.borderRadius['2xl']};
  overflow: hidden;
  border: 1px solid ${getColor('primary.100')};

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    min-height: 300px;
  }
  @media (max-width: 480px) {
    min-height: 250px;
  }
`;

// ─── Footer (matching HomePage) ────────────────────────────────────────────
const FooterButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  min-height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  transition: color ${DesignSystem.motion.duration.fast} ease;

  &:hover { color: rgba(255, 255, 255, 1); }
  &:active { opacity: 0.7; }
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FooterSocialLink = styled.a`
  color: rgba(255, 255, 255, 0.85);
  transition: color ${DesignSystem.motion.duration.fast} ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('sm')};
  min-height: 44px;
  min-width: 44px;

  &:hover { color: rgba(255, 255, 255, 1); }
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export default function ContactPage({ onNavigateTo }: ContactPageProps) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ─── Header Dropdown States (centralized pattern) ──────────────────────
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  return (
    <PageContainer>
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="contact"
        transparent
        forceScrolled
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <FadeInContainer>
        <HeroSection>
          <PageTitle>Get in Touch</PageTitle>
          <PageSubtitle>
            Ready to plan your Himalayan adventure? We're here to help.
          </PageSubtitle>
          <GoldLine />
        </HeroSection>
      </FadeInContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── Contact Info + Map side by side ──────────────────────────── */}
      <Container maxWidth="lg">
        <FadeInUpContainer delay={0.15}>
          <ContactContent>
            <ContactGrid>
              {/* Left — Contact Info */}
              <ContactInfoCard>
                <CompanyName>
                  <h3>
                    HIMALAYAN <span className="brand-accent">ROVERS</span>
                  </h3>
                </CompanyName>

                <InfoItem>
                  <span className="label">Location</span>
                  <p className="value">Near Hotel Kesar, Fort Road, Leh, Ladakh - 194101</p>
                </InfoItem>

                <InfoItem>
                  <span className="label">Phone</span>
                  <p className="value">+91 6005365063</p>
                  <p className="value">+91 9419227555</p>
                </InfoItem>

                <InfoItem>
                  <span className="label">Email</span>
                  <a className="value" href="mailto:himalayanroversofficial@gmail.com">
                    himalayanroversofficial@gmail.com
                  </a>
                </InfoItem>

                <InfoItem>
                  <span className="label">Hours</span>
                  <p className="value">Mon – Sun: 8:00 AM – 8:00 PM IST</p>
                </InfoItem>
              </ContactInfoCard>

              {/* Right — Map */}
              <MapContainer>
                <iframe
                  title="Himalayan Rovers Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3297.9889147046786!2d77.5769!3d34.1640!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38ff5f5c1c1c1c1d%3A0x1c1c1c1c1c1c1c1c!2sLadakh%2C%20UT!5e0!3m2!1sen!2sin!4v1234567890"
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </MapContainer>
            </ContactGrid>
          </ContactContent>
        </FadeInUpContainer>
      </Container>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── Footer (matching HomePage) ──────────────────────────────── */}
      <footer style={{
        background: '#3D3721',
        color: 'white',
        padding: `${getSpacing('2xl')} 0`,
        textAlign: 'center' as const,
      }}>
        <Container maxWidth="lg">
          <BodyText style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join thousands of travelers who have discovered the magic of the Himalayas with us.
          </BodyText>
          <Spacer size="lg" />
          <Flex justify="center" gap="lg" wrap>
            <FooterButton onClick={() => onNavigateTo?.('home')}>Home</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('about')}>About</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('curated-experiences')}>Explore</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('reviews')}>Reviews</FooterButton>
          </Flex>
          <Spacer size="lg" />
          <Flex justify="center" gap="md" style={{ marginBottom: getSpacing('md') }}>
            <FooterSocialLink href="https://www.instagram.com/himalayan__rovers/" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </FooterSocialLink>
          </Flex>
          <Caption style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            © 2026 Himalayan Rovers. All rights reserved.
          </Caption>
        </Container>
      </footer>
    </PageContainer>
  );
}
